import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Calendar, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTour } from './TourProvider';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const CARD_W = 360;
const GAP = 16;
const CARD_H_EST = 210;
const BOOK_DEMO = 'https://meetings-na2.hubspot.com/christian-carroll';

const same = (a: Rect | null, b: Rect | null) => {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
};

export function TourOverlay() {
  const { running, step, index, total, next, prev, end } = useTour();
  const [rect, setRect] = useState<Rect | null>(null);
  const rectRef = useRef<Rect | null>(null);
  const rafRef = useRef<number | null>(null);
  const scrolledFor = useRef<string | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [cardH, setCardH] = useState(0);

  const commit = useCallback((r: Rect | null) => {
    if (same(rectRef.current, r)) return;
    rectRef.current = r;
    setRect(r);
  }, []);

  // Measure the target — event driven (scroll/resize/DOM changes) instead of a
  // permanent rAF loop, so the page stays smooth.
  useEffect(() => {
    if (!running || !step) {
      commit(null);
      return;
    }
    if (!step.target) {
      commit(null);
      scrolledFor.current = null;
      return;
    }

    let stopped = false;

    const measure = () => {
      if (stopped) return;
      const el = document.querySelector(step.target!) as HTMLElement | null;
      if (!el) {
        commit(null);
        return;
      }
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) {
        commit(null);
        return;
      }
      const pad = step.padding ?? 8;
      // Keep the spotlight inside the viewport so the card never floats off-screen.
      const top = Math.max(8, r.top - pad);
      const left = Math.max(8, r.left - pad);
      const height = Math.min(r.height + pad * 2, window.innerHeight - top - 8);
      const width = Math.min(r.width + pad * 2, window.innerWidth - left - 8);
      commit({ top, left, width, height });

      if (scrolledFor.current !== step.id) {
        scrolledFor.current = step.id;
        const outOfView = r.top < 96 || r.bottom > window.innerHeight - 96;
        if (outOfView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    };

    const schedule = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        measure();
      });
    };

    measure();
    // Re-measure a few times while the route/section settles in.
    const timers = [60, 180, 360, 700, 1100].map((ms) => window.setTimeout(measure, ms));

    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const ro = new ResizeObserver(schedule);
    ro.observe(document.documentElement);
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      stopped = true;
      timers.forEach(clearTimeout);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      ro.disconnect();
      mo.disconnect();
    };
  }, [running, step, commit]);

  // Click a helper element (e.g. a sub-tab) when the step asks for it.
  useEffect(() => {
    if (!running || !step?.clickBefore) return;
    const t = setTimeout(() => {
      const el = document.querySelector(step.clickBefore!) as HTMLElement | null;
      el?.click();
    }, 300);
    return () => clearTimeout(t);
  }, [running, step]);

  // Measure the card so placement math never guesses (prevents overlap).
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setCardH(el.offsetHeight));
    ro.observe(el);
    setCardH(el.offsetHeight);
    return () => ro.disconnect();
  }, [running, step]);

  const pos = useMemo(() => {
    const vw = typeof window === 'undefined' ? 1280 : window.innerWidth;
    const vh = typeof window === 'undefined' ? 800 : window.innerHeight;
    const width = Math.min(CARD_W, vw - 32);
    const h = cardH || CARD_H_EST;

    if (!rect) {
      return {
        style: {
          width,
          transform: `translate3d(${Math.round((vw - width) / 2)}px, ${Math.round(vh / 2 - h / 2)}px, 0)`,
        } as React.CSSProperties,
        placement: null as null | 'top' | 'bottom' | 'left' | 'right',
        arrow: 0,
      };
    }

    const spaceBelow = vh - (rect.top + rect.height);
    const spaceAbove = rect.top;
    const spaceRight = vw - (rect.left + rect.width);
    const spaceLeft = rect.left;

    const fits = {
      bottom: spaceBelow >= h + GAP + 12,
      top: spaceAbove >= h + GAP + 12,
      right: spaceRight >= width + GAP + 12,
      left: spaceLeft >= width + GAP + 12,
    };

    const requested = step?.placement && step.placement !== 'auto' ? step.placement : null;
    const order: Array<'bottom' | 'top' | 'right' | 'left'> = requested
      ? ([requested, 'bottom', 'top', 'right', 'left'].filter(
          (p, i, a) => a.indexOf(p) === i
        ) as Array<'bottom' | 'top' | 'right' | 'left'>)
      : ['bottom', 'top', 'right', 'left'];

    let placement = order.find((p) => fits[p]) ?? null;
    if (!placement) {
      // Nothing fits cleanly — pick the side with the most room.
      const best = (
        [
          ['bottom', spaceBelow],
          ['top', spaceAbove],
          ['right', spaceRight],
          ['left', spaceLeft],
        ] as Array<['bottom' | 'top' | 'right' | 'left', number]>
      ).sort((a, b) => b[1] - a[1])[0];
      placement = best[0];
    }

    let top: number;
    let left: number;

    switch (placement) {
      case 'top':
        top = rect.top - GAP - h;
        left = rect.left + rect.width / 2 - width / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - h / 2;
        left = rect.left - width - GAP;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - h / 2;
        left = rect.left + rect.width + GAP;
        break;
      default:
        top = rect.top + rect.height + GAP;
        left = rect.left + rect.width / 2 - width / 2;
    }

    // Keep on screen.
    if (placement === 'top' || placement === 'bottom') {
      left = Math.min(Math.max(16, left), Math.max(16, vw - width - 16));
      top = Math.min(Math.max(16, top), Math.max(16, vh - h - 16));
    } else {
      top = Math.min(Math.max(16, top), Math.max(16, vh - h - 16));
      left = Math.min(Math.max(16, left), Math.max(16, vw - width - 16));
    }

    // Final guard: never cover the spotlighted content.
    const overlaps =
      left < rect.left + rect.width && left + width > rect.left && top < rect.top + rect.height && top + h > rect.top;
    if (overlaps) {
      const shiftDown = rect.top + rect.height + GAP;
      const shiftUp = rect.top - h - GAP;
      const shiftRight = rect.left + rect.width + GAP;
      const shiftLeft = rect.left - width - GAP;
      if (shiftDown + h <= vh - 16) {
        top = shiftDown;
        placement = 'bottom';
      } else if (shiftUp >= 16) {
        top = shiftUp;
        placement = 'top';
      } else if (shiftRight + width <= vw - 16) {
        left = shiftRight;
        placement = 'right';
      } else if (shiftLeft >= 16) {
        left = shiftLeft;
        placement = 'left';
      }
    }

    // Where the little pointer should sit along the card edge.
    const targetCx = rect.left + rect.width / 2;
    const targetCy = rect.top + rect.height / 2;
    const arrow =
      placement === 'top' || placement === 'bottom'
        ? Math.min(Math.max(targetCx - left, 22), width - 22)
        : Math.min(Math.max(targetCy - top, 22), h - 22);

    return {
      style: { width, transform: `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)` } as React.CSSProperties,
      placement,
      arrow,
    };
  }, [rect, step, cardH]);

  const cardStyle = pos.style;


  if (!running || !step) return null;
  const isLast = index === total - 1;

  return createPortal(
    <div className="fixed inset-0 z-[120]">
      {/* Dim layer: one element, one paint. Spotlight is the hole. */}
      {rect ? (
        <div
          className="pointer-events-auto absolute rounded-[14px] transition-[transform,width,height] duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
          onClick={end}
          style={{
            top: 0,
            left: 0,
            width: rect.width,
            height: rect.height,
            transform: `translate3d(${Math.round(rect.left)}px, ${Math.round(rect.top)}px, 0)`,
            boxShadow: `0 0 0 100vmax hsl(var(--background) / 0.66), inset 0 0 0 1px hsl(var(--foreground) / 0.08), 0 0 0 1px hsl(var(--foreground) / 0.06), 0 24px 70px -30px hsl(var(--foreground) / 0.25)`,
          }}
        />
      ) : (
        <div className="pointer-events-auto absolute inset-0 bg-background/70" onClick={end} />
      )}

      {/* Step card */}
      <div
        className="pointer-events-auto absolute left-0 top-0 rounded-2xl border border-border/80 bg-card/95 p-5 shadow-[0_30px_80px_-30px_hsl(var(--foreground)/0.35)] backdrop-blur-sm transition-transform duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
        style={cardStyle}
        ref={cardRef}
      >
        {pos.placement && (
          <span
            aria-hidden
            className="pointer-events-none absolute h-3 w-3 rotate-45 border border-border/80 bg-card"
            style={
              pos.placement === 'bottom'
                ? { top: -6.5, left: pos.arrow - 6, borderRight: 'none', borderBottom: 'none' }
                : pos.placement === 'top'
                  ? { bottom: -6.5, left: pos.arrow - 6, borderLeft: 'none', borderTop: 'none' }
                  : pos.placement === 'right'
                    ? { left: -6.5, top: pos.arrow - 6, borderRight: 'none', borderTop: 'none' }
                    : { right: -6.5, top: pos.arrow - 6, borderLeft: 'none', borderBottom: 'none' }
            }
          />
        )}
        <button
          onClick={end}
          aria-label="Close tour"
          className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-primary">
          <Sparkles className="h-3.5 w-3.5" />
          Product tour
        </div>
        <h3 className="mt-2 pr-6 font-display text-lg leading-snug text-foreground">{step.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.body}</p>

        {/* Progress */}
        <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
            style={{ width: `${((index + 1) / total) * 100}%` }}
          />
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-xs tabular-nums text-muted-foreground">
            {index + 1} / {total}
          </span>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <Button variant="ghost" size="sm" className="gap-1 rounded-lg" onClick={prev}>
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </Button>
            )}
            {isLast ? (
              <>
                <Button variant="ghost" size="sm" className="rounded-lg" onClick={end}>
                  Done
                </Button>
                <Button asChild size="sm" className="gap-1.5 rounded-lg">
                  <a href={BOOK_DEMO} target="_blank" rel="noopener noreferrer">
                    <Calendar className="h-3.5 w-3.5" />
                    Book a demo
                  </a>
                </Button>
              </>
            ) : (
              <Button size="sm" className="gap-1 rounded-lg" onClick={next}>
                Next
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {index === 0 && (
          <button onClick={end} className="mt-3 text-xs text-muted-foreground underline-offset-2 hover:underline">
            Skip tour
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}

export default TourOverlay;
